/**
 * ElementPalette Component
 *
 * Sidebar palette of available flow elements.
 * Elements can be clicked to add to the canvas.
 *
 * @component
 */

import React, { useState } from 'react';
import {
  Monitor,
  GitBranch,
  Equal,
  RefreshCw,
  Download,
  PlusCircle,
  Edit2,
  Trash2,
  Mail,
  MessageSquare,
  Clock,
  Zap,
  Database,
  Search,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { StandardCard, StandardSearchBar } from '../../standard';
import { ELEMENT_TYPE_DEFINITIONS } from '../../../services/flowAutomationStorage';
import type { ElementType, FlowType } from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface ElementPaletteProps {
  flowType: FlowType;
  onAddElement: (type: ElementType) => void;
  disabled?: boolean;
}

interface ElementCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  elements: ElementType[];
}

// ============================================
// CONSTANTS
// ============================================

const ELEMENT_CATEGORIES: ElementCategory[] = [
  {
    id: 'interaction',
    label: 'Interaction',
    icon: <Monitor size={16} />,
    elements: ['screen']
  },
  {
    id: 'logic',
    label: 'Logic',
    icon: <GitBranch size={16} />,
    elements: ['decision', 'assignment', 'loop']
  },
  {
    id: 'data',
    label: 'Data',
    icon: <Database size={16} />,
    elements: ['get_records', 'create_records', 'update_records', 'delete_records']
  },
  {
    id: 'actions',
    label: 'Actions',
    icon: <Zap size={16} />,
    elements: ['email_alert', 'post_to_feed', 'action', 'subflow']
  },
  {
    id: 'timing',
    label: 'Timing',
    icon: <Clock size={16} />,
    elements: ['wait']
  }
];

// ============================================
// ELEMENT ICON HELPER
// ============================================

const getElementIcon = (type: ElementType, size: number = 20) => {
  switch (type) {
    case 'screen':
      return <Monitor size={size} className="text-blue-500" />;
    case 'decision':
      return <GitBranch size={size} className="text-yellow-500" />;
    case 'assignment':
      return <Equal size={size} className="text-purple-500" />;
    case 'loop':
      return <RefreshCw size={size} className="text-orange-500" />;
    case 'get_records':
      return <Download size={size} className="text-cyan-500" />;
    case 'create_records':
      return <PlusCircle size={size} className="text-green-500" />;
    case 'update_records':
      return <Edit2 size={size} className="text-blue-500" />;
    case 'delete_records':
      return <Trash2 size={size} className="text-red-500" />;
    case 'email_alert':
      return <Mail size={size} className="text-pink-500" />;
    case 'post_to_feed':
      return <MessageSquare size={size} className="text-indigo-500" />;
    case 'wait':
      return <Clock size={size} className="text-gray-500" />;
    case 'action':
      return <Zap size={size} className="text-yellow-500" />;
    case 'subflow':
      return <Database size={size} className="text-teal-500" />;
    default:
      return <Zap size={size} className="text-gray-500" />;
  }
};

// ============================================
// ELEMENT ITEM COMPONENT
// ============================================

interface ElementItemProps {
  type: ElementType;
  label: string;
  description: string;
  available: boolean;
  onClick: () => void;
  disabled: boolean;
}

const ElementItem: React.FC<ElementItemProps> = ({
  type,
  label,
  description,
  available,
  onClick,
  disabled
}) => {
  const isDisabled = disabled || !available;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`w-full p-3 rounded-lg text-left transition-all ${
        isDisabled
          ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
          : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${
          isDisabled
            ? 'bg-gray-100 dark:bg-gray-700'
            : 'bg-white dark:bg-gray-700 shadow-sm'
        }`}>
          {getElementIcon(type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${
            isDisabled
              ? 'text-gray-400 dark:text-gray-500'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {label}
          </p>
          <p className={`text-xs mt-0.5 ${
            isDisabled
              ? 'text-gray-400 dark:text-gray-600'
              : 'text-gray-500 dark:text-gray-400'
          }`}>
            {description}
          </p>
          {!available && (
            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
              Not available for this flow type
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// ============================================
// CATEGORY SECTION COMPONENT
// ============================================

interface CategorySectionProps {
  category: ElementCategory;
  flowType: FlowType;
  searchQuery: string;
  onAddElement: (type: ElementType) => void;
  disabled: boolean;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  flowType,
  searchQuery,
  onAddElement,
  disabled
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Filter elements by search query
  const filteredElements = category.elements.filter(type => {
    const def = ELEMENT_TYPE_DEFINITIONS.find(d => d.type === type);
    if (!def) return false;
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      def.label.toLowerCase().includes(query) ||
      def.description.toLowerCase().includes(query) ||
      type.toLowerCase().includes(query)
    );
  });

  if (filteredElements.length === 0) return null;

  return (
    <div className="mb-2">
      {/* Category Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
      >
        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="flex items-center gap-2">
          {category.icon}
          {category.label}
        </span>
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          {filteredElements.length}
        </span>
      </button>

      {/* Category Elements */}
      {isExpanded && (
        <div className="mt-1 space-y-1 pl-2">
          {filteredElements.map(type => {
            const def = ELEMENT_TYPE_DEFINITIONS.find(d => d.type === type);
            if (!def) return null;

            const isAvailable = def.availableIn.includes(flowType);

            return (
              <ElementItem
                key={type}
                type={type}
                label={def.label}
                description={def.description}
                available={isAvailable}
                onClick={() => onAddElement(type)}
                disabled={disabled}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ElementPalette: React.FC<ElementPaletteProps> = ({
  flowType,
  onAddElement,
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Count available elements
  const availableCount = ELEMENT_TYPE_DEFINITIONS.filter(
    def => def.availableIn.includes(flowType)
  ).length;

  return (
    <StandardCard className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Elements
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {availableCount} elements available
        </p>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search elements..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Element Categories */}
      <div className="flex-1 overflow-y-auto p-2">
        {ELEMENT_CATEGORIES.map(category => (
          <CategorySection
            key={category.id}
            category={category}
            flowType={flowType}
            searchQuery={searchQuery}
            onAddElement={onAddElement}
            disabled={disabled}
          />
        ))}

        {/* No results */}
        {searchQuery && ELEMENT_CATEGORIES.every(cat =>
          cat.elements.every(type => {
            const def = ELEMENT_TYPE_DEFINITIONS.find(d => d.type === type);
            if (!def) return true;
            const query = searchQuery.toLowerCase();
            return !(
              def.label.toLowerCase().includes(query) ||
              def.description.toLowerCase().includes(query) ||
              type.toLowerCase().includes(query)
            );
          })
        ) && (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No elements match "{searchQuery}"
            </p>
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Click an element to add it to your flow. Some elements may not be available for this flow type.
        </p>
      </div>
    </StandardCard>
  );
};

export default ElementPalette;
